<template>
  <div class="rtb-stats__widget">
    <template v-if="hasRtb">
      <SmartTableWidget
        :widget="{
          tableTitle: $t('RTB_REJECTION_BREAKDOWN_PANE_TITLE'),
          iconClass: 'fa fa-trophy',
          locale: $options.i18n
        }"
        :date-range="rejectionRange"
        :column-defs="columnSettings.errorsByPublisher"
        :row-data="errorsByPublisher || []"
        :enable-export="true"
        :export-title="formatTitleWithCampaignName('RTB Errors Stats by Publisher')"
        icon="fa-chevron-down"
        @update-date-range="updateErrors"
      >
        <template #table>
          <LoadingStatusDisplay
            v-if="loadingErrorBreakdown"
            :loading="true"
          />

          <div
            v-else-if="!errorsByPublisher.length"
            class="empty-records"
          >
            {{ $t('RTB_REJECTION_BREAKDOWN_PANE_EMPTY') }}
          </div>
        </template>
      </SmartTableWidget>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import messages from '../locales';
import sharedMessages from '@/core/locales';
import SmartTableWidget from '@/core/components/SmartTable/SmartTableWidget.vue';
import {
  GET_RTB_ERROR_STATS,
  GET_CAMPAIGN_RTB_FAIL_REASONS,
  GET_CAMPAIGN_RTB_FAIL_REASONS_FOR_PUBLISHER
} from '@/modules/rtbStats/store/actionTypes';
import { toRequestParams } from '@/modules/rtbStats/store/utils';
import LoadingStatusDisplay from '@/core/components/Loader/LoadingStatusDisplay.vue';
import { STATUS_PENDING, STATUS_SUCCESS } from '@/store/helpers';


/**
 * @type {Record<"errorsByPublisher" | "tableData",
 * [import('@ag-grid-community/core').ColDef
 *  & {field: (data.structures.RtbBreakdownByPublisherTableKeys | data.structures.RtbErrorTableKeys) }]>}
 */
import { columnSettings } from '../columnSettings';
import cloneDeep from 'lodash/cloneDeep';

export default {
  components: { SmartTableWidget, LoadingStatusDisplay },
  props: {
    campaign: {
      type: Object,
      default: () => {
        return {};
      }
    },
    isAffiliate: {
      type: Boolean,
      default: null
    }
  },
  data: () => ({
    rejectionRange: 'Today',
    columnSettings
  }),
  computed: {
    ...mapGetters('rtbStats', [
      'statusSummary',
    ]),

    ...mapState('rtbStats', [
      'affiliatesErrors'
    ]),

    ...mapState({
      flags: 'currentAccountFeatureFlags'
    }),
    baseStartRequestBody() {
      return { past: 0, days: 1, campaignId: this.campaign.id };
    },
    loadedErrors() {
      return this.statusSummary.GET_RTB_ERROR_STATS == STATUS_SUCCESS;
    },
    loadingErrorBreakdown() {
      return this.statusSummary.GET_RTB_ERROR_STATS === STATUS_PENDING;
    },

    errors() {
      return {
        rejections: this.$store.state.rtbStats.status
          ?.GET_RTB_ERROR_STATS_Status?.error
      };
    },

    /** @type {() => *[]} */
    errorsByPublisher() {
      if (!this.loadedErrors || !this.affiliatesErrors?.length)
        return [];

      return Array.from(cloneDeep(this.affiliatesErrors), this.toTableFormat);
    },
    /**
     *  Parameters based on user settings.
     *  This is watched, once changed will trigger the action from the store
     *  @type {() => data.structures.RtbStatsRequestParams}
     */
    requestParamsRej() {
      return toRequestParams(this.rejectionRange);
    },
    /**
     * Checks if the user has rtb enabled
     * @type {() => boolean}
     */
    hasRtb() {
      return !!this.flags.rtb;
    }
  },
  /**
   * When mounted, pull the data needed
   */
  mounted() {
    if(this.isAffiliate) {
      this.getCampaignFailReasonsForPublisher(this.baseStartRequestBody);
    }
    else if(!this.campaign.id) {
      this.getErrors({ past: 0, days: 1 });
    } else {
      this.getCampaignFailReasons(this.baseStartRequestBody);
    }
  },
  methods: {
    ...mapActions('rtbStats', {
      getErrors: GET_RTB_ERROR_STATS,
      getCampaignFailReasons: GET_CAMPAIGN_RTB_FAIL_REASONS,
      getCampaignFailReasonsForPublisher: GET_CAMPAIGN_RTB_FAIL_REASONS_FOR_PUBLISHER
    }),

    updateErrors(label) {
      const params = toRequestParams(label);
      if(this.isAffiliate) {
        this.getCampaignFailReasonsForPublisher({ past: params.past, days: params.days, campaignId: this.campaign.id });
      }
      else if(!this.campaign.id) {
        this.getErrors(params);
      } else {
        this.getCampaignFailReasons({ past: params.past, days: params.days, campaignId: this.campaign.id });
      }
    },
    toTableFormat(publisher) {
      const { id, name, values }=publisher;
      if(this.campaign.id) {
        // Formatting it here makes possible to export in the right format already
        values.rtbwinningbidpercent = values.rtbwinningbidpercent*100;
        values.rtbacceptedwinningbidpercent = values.rtbacceptedwinningbidpercent*100;
      }
      return {
        affiliateId: id,
        affiliateName: name,
        ...values
      };
    },
    formatTitleWithCampaignName(baseTitle) {
      let increment = '';
      if(this.campaign.name) {
        increment = ' - ' + this.campaign.name;
      }
      return baseTitle + increment;
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  }
};
</script>

<style lang="scss" scoped>

</style>
